import type { OrganizationListItem } from "@pilatestime-admin/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Building2 } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchOrganizationsQueryOptions } from "@/api/organizations";
import { DataTable } from "@/components/data-table";
import { useApiParameters } from "@/hooks/useApiParameters";

export const Route = createFileRoute("/_auth/app/_app/organizations/")({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);

		// Preload organizations data
		context.queryClient.ensureQueryData(fetchOrganizationsQueryOptions(token));

		return { userId, token };
	},
	component: OrganizationsPage,
});

function OrganizationsPage() {
	const { t } = useTranslation();
	const { token } = Route.useLoaderData();
	const navigate = useNavigate();

	const {
		data: organizationsData,
		isLoading,
		error,
	} = useSuspenseQuery(fetchOrganizationsQueryOptions(token));

	const handleRowClick = useCallback(
		(organization: OrganizationListItem) => {
			navigate({
				to: "/app/organizations/$organizationId",
				params: { organizationId: organization.id },
			});
		},
		[navigate],
	);

	const columns: ColumnDef<OrganizationListItem>[] = useMemo(
		() => [
			{
				accessorKey: "name",
				header: t("organizations.table.name"),
				cell: ({ row }) => (
					<div className="font-medium">{row.original.name}</div>
				),
			},
			{
				accessorKey: "description",
				header: t("organizations.table.description"),
				cell: ({ row }) => (
					<div className="max-w-xs truncate text-base-content/70">
						{row.original.description || "-"}
					</div>
				),
				meta: { hiddenOnMobile: true },
			},
			{
				accessorKey: "defaultLanguage",
				header: t("organizations.table.language"),
				cell: ({ row }) => (
					<span className="badge badge-ghost badge-sm">
						{row.original.defaultLanguage}
					</span>
				),
				meta: { hiddenOnMobile: true },
			},
			{
				accessorKey: "defaultCurrency",
				header: t("organizations.table.currency"),
				cell: ({ row }) => (
					<span className="badge badge-ghost badge-sm">
						{row.original.defaultCurrency}
					</span>
				),
				meta: { hiddenOnMobile: true },
			},
			{
				accessorKey: "isTrialAvailable",
				header: t("organizations.table.trialAvailable"),
				cell: ({ row }) => (
					<span
						className={`badge badge-sm ${
							row.original.isTrialAvailable ? "badge-success" : "badge-neutral"
						}`}
					>
						{row.original.isTrialAvailable
							? t("organizations.trial.yes")
							: t("organizations.trial.no")}
					</span>
				),
			},
			{
				accessorKey: "createdAt",
				header: t("organizations.table.createdAt"),
				cell: ({ row }) => {
					const date = new Date(row.original.createdAt);
					return date.toLocaleDateString();
				},
				meta: { hiddenOnMobile: true },
			},
		],
		[t],
	);

	return (
		<div className="container mx-auto p-6 space-y-6">
			{/* Page Title */}
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-base-content">
					{t("organizations.title")}
				</h1>
			</div>

			{/* Organizations Table */}
			<div className="card bg-base-100 shadow-sm">
				<div className="card-body p-0">
					<DataTable
						data={organizationsData?.organizations || []}
						columns={columns}
						isLoading={isLoading}
						error={error}
						testId="organizations"
						emptyState={{
							title: t("organizations.empty.title"),
							description: t("organizations.empty.description"),
							icon: Building2,
						}}
						onRowClick={handleRowClick}
					/>
				</div>
			</div>
		</div>
	);
}
